import React, { useState } from "react";
import { Select, Slider, DatePicker, Switch } from "antd";
import { toClass, defaultProps, compose } from "recompose";
import * as R from "ramda";
import moment from "moment-timezone";

import styled from "styled-components/macro";

const { MonthPicker } = DatePicker;

const FormationInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
  margin: 5px 0 15px;
  padding-right: 5px;
  flex: 1;
  label {
    margin-bottom: 5px;
  }
  .ant-slider {
    width: 95%;
    width: calc(100% - 15px);
  }
  .slider-container {
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: center;

    > span {
      width: 70px;
      text-align: center;
    }
  }
  .row {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    width: 100%;
    .switch-wrapper {
      padding: 11px 10px 15px;
      &.active {
        background-color: #f8f9fa;
      }
    }
    .ant-switch {
      margin-bottom: 6px;
    }
    .input-wrapper {
      margin-right: 10px;
      margin-bottom: 15px;
    }
  }
  .input-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1;
    & + .input-wrapper {
      margin-left: 15px;
    }
  }
  .advanced-row {
    padding: 13px 10px;
    background-color: #f8f9fa;
  }
  .flex-row {
    display: flex;
    flex-direction: row;
  }
  .flex-1 {
    flex: 1;
  }
`;

function FormationInput({ t, value, options, onChange, disabled }) {
  const { level, from, to, progress } = value;

  const [advanced, setAdvanced] = useState(Boolean(from || to || progress));

  const baseParameters = {
    from: null,
    to: null,
    progress: null
  };

  const getDefaultParameters = (level, advanced) => {
    if (advanced) {
      if (level && level.match("avanzado")) {
        return {
          ...baseParameters,
          progress: [0, 100]
        };
      }
      if (level && level.match("completo")) {
        return {
          ...baseParameters,
          to: moment
            .utc()
            .startOf("day")
            .valueOf()
        };
      }
    }
    return baseParameters;
  };

  const setLevel = (newLevel, defaults = baseParameters) => {
    if (!newLevel) return;
    if (newLevel !== level) {
      setAdvanced(false);
    }
    onChange({
      level: newLevel,
      ...defaults
    });
  };

  const switchAdvanced = () => {
    if (advanced) {
      setAdvanced(false);
      setLevel(level);
      return;
    }
    setAdvanced(true);
    setLevel(level, getDefaultParameters(level, true));
  };

  const showAdvancedOptions = !disabled && advanced && level;

  return (
    <FormationInputWrapper>
      <div className="row">
        <div className="input-wrapper">
          <label>{t("global:educationLevel")}</label>
          <Select
            filterOption
            optionFilterProp="children"
            value={
              level ? t(`catalogs:educationLevelsAdvanced.${level}`) : undefined
            }
            disabled={disabled}
            onChange={level => setLevel(level)}
          >
            {options.map(level => (
              <Select.Option key={level} value={level}>
                {t(`catalogs:educationLevelsAdvanced.${level}`)}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className={`switch-wrapper ${advanced ? "active" : ""}`}>
          <Switch
            checked={advanced}
            onClick={switchAdvanced}
            disabled={!level || disabled}
            checkedChildren={t("opportunity:details.configuration")}
            unCheckedChildren={t("opportunity:details.configuration")}
          />
        </div>
      </div>

      {showAdvancedOptions && progress && level.match("avanzado") ? (
        <div className="advanced-row input-wrapper flex-1">
          <label>{t("opportunity:details.coursedPercentage")}</label>
          <div className="slider-container">
            <span>{value.progress[0]}%</span>
            <Slider
              range
              value={value.progress}
              onChange={newValue =>
                onChange(R.assoc("progress", newValue, value))
              }
              min={0}
              max={100}
              step={1}
              tipFormatter={value => `${value}%`}
            />
            <span>{value.progress[1]}%</span>
          </div>
        </div>
      ) : showAdvancedOptions && level.match("completo") ? (
        <div className="advanced-row flex-1 flex-row">
          <div className="input-wrapper">
            <label>{t("opportunity:details.graduatedFrom")}</label>
            <MonthPicker
              defaultValue={null}
              style={{ width: "100%" }}
              placeholder={t("opportunity:details.datePlaceholder")}
              value={value.from ? moment.utc(value.from) : null}
              onChange={newValue =>
                onChange(
                  R.assoc(
                    "from",
                    newValue
                      ? newValue
                          .utc()
                          .startOf("day")
                          .valueOf()
                      : null,
                    value
                  )
                )
              }
              format="MMMM, YYYY"
              disabledDate={calendarDate =>
                calendarDate.valueOf() > Date.now() ||
                calendarDate.valueOf() >= to
              }
            />
          </div>
          <div className="input-wrapper">
            <label>{t("opportunity:details.graduatedTo")}</label>
            <MonthPicker
              defaultValue={null}
              style={{ width: "100%" }}
              placeholder={t("opportunity:details.datePlaceholder")}
              value={value.to ? moment.utc(value.to) : null}
              onChange={newValue =>
                onChange(
                  R.assoc(
                    "to",
                    newValue
                      ? newValue
                          .utc()
                          .startOf("day")
                          .valueOf()
                      : null,
                    value
                  )
                )
              }
              format="MMMM, YYYY"
              disabledDate={calendarDate => calendarDate.valueOf() <= from}
            />
          </div>
        </div>
      ) : null}
    </FormationInputWrapper>
  );
}

export default compose(
  toClass,
  defaultProps({
    value: {
      level: null,
      to: null,
      from: null,
      progress: null
    },
    options: [],
    disabled: false
  })
)(FormationInput);
